import { useState } from 'react';
import { Calculator, DollarSign, Clock, TrendingUp } from 'lucide-react';

export function ROICalculatorSection() {
  const [inputs, setInputs] = useState({
    accounts: 10,
    annualVolume: 50000,
    hoursPerWeek: 20,
    hourlyCost: 75,
  });

  // Calculate outputs
  const currentAnnualCost = inputs.hoursPerWeek * 52 * inputs.hourlyCost;
  const expectedFineExposure = inputs.accounts * 25000; // $25K per account at risk
  const timeSavedPercent = 80;
  const timeSaved = Math.round(inputs.hoursPerWeek * (timeSavedPercent / 100));
  const costSavings = timeSaved * 52 * inputs.hourlyCost;
  const totalPotentialSavings = costSavings + expectedFineExposure * 0.5; // 50% risk reduction
  const roiPercent = Math.round((totalPotentialSavings / 18000) * 100); // Assuming $1500/mo cost

  const handleChange = (field: keyof typeof inputs, value: number) => {
    setInputs((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section id="roi" className="relative py-20 md:py-28 bg-white">
      <div className="container relative">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-4 py-1.5 text-sm mb-6">
            <Calculator className="h-4 w-4 text-purple-700" />
            <span className="text-slate-700 font-medium">ROI Calculator</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-slate-900">
            Calculate Your{' '}
            <span className="text-purple-600">
              Potential Savings
            </span>
          </h2>
          <p className="text-lg text-slate-600">
            See how much PositionIQ could save your organization in time and compliance risk.
          </p>
        </div>

        <div className="mx-auto max-w-5xl grid gap-8 lg:grid-cols-2">
          {/* Input Panel */}
          <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 shadow-sm">
            <h3 className="font-semibold text-lg mb-6 text-slate-900">Your Current Situation</h3>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700">
                  Number of trading accounts
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={inputs.accounts}
                  onChange={(e) => handleChange('accounts', parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-600"
                />
                <div className="flex justify-between text-sm text-slate-500 mt-1">
                  <span>1</span>
                  <span className="font-semibold text-purple-700 text-lg">{inputs.accounts}</span>
                  <span>50+</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700">
                  Annual trading volume (contracts)
                </label>
                <input
                  type="range"
                  min="1000"
                  max="500000"
                  step="1000"
                  value={inputs.annualVolume}
                  onChange={(e) => handleChange('annualVolume', parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-600"
                />
                <div className="flex justify-between text-sm text-slate-500 mt-1">
                  <span>1K</span>
                  <span className="font-semibold text-purple-700 text-lg">{(inputs.annualVolume / 1000).toFixed(0)}K</span>
                  <span>500K+</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700">
                  Hours/week on compliance tasks
                </label>
                <input
                  type="range"
                  min="5"
                  max="60"
                  value={inputs.hoursPerWeek}
                  onChange={(e) => handleChange('hoursPerWeek', parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-600"
                />
                <div className="flex justify-between text-sm text-slate-500 mt-1">
                  <span>5 hrs</span>
                  <span className="font-semibold text-purple-700 text-lg">{inputs.hoursPerWeek} hrs</span>
                  <span>60 hrs</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700">
                  Hourly compliance cost ($)
                </label>
                <input
                  type="range"
                  min="25"
                  max="200"
                  step="5"
                  value={inputs.hourlyCost}
                  onChange={(e) => handleChange('hourlyCost', parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-600"
                />
                <div className="flex justify-between text-sm text-slate-500 mt-1">
                  <span>$25</span>
                  <span className="font-semibold text-purple-700 text-lg">${inputs.hourlyCost}</span>
                  <span>$200</span>
                </div>
              </div>
            </div>
          </div>

          {/* Output Panel */}
          <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 shadow-sm">
            <h3 className="font-semibold text-lg mb-6 text-slate-900">Your Potential Savings</h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-200">
                    <DollarSign className="h-5 w-5 text-slate-600" />
                  </div>
                  <span className="text-sm text-slate-600">Current annual compliance cost</span>
                </div>
                <span className="font-semibold text-slate-900">${currentAnnualCost.toLocaleString()}</span>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-red-50 border border-red-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100">
                    <DollarSign className="h-5 w-5 text-red-600" />
                  </div>
                  <span className="text-sm text-slate-600">Expected fine exposure risk</span>
                </div>
                <span className="font-semibold text-red-600">${expectedFineExposure.toLocaleString()}</span>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <Clock className="h-5 w-5 text-emerald-600" />
                  </div>
                  <span className="text-sm text-slate-600">Time saved per week ({timeSavedPercent}% reduction)</span>
                </div>
                <span className="font-semibold text-emerald-600">{timeSaved} hours</span>
              </div>

              <div className="border-t border-slate-200 pt-4 mt-4">
                <div className="flex items-center justify-between p-5 rounded-xl bg-purple-50 border border-purple-200">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-600">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-medium text-slate-900">Total potential annual savings</span>
                  </div>
                  <span className="text-2xl font-bold text-purple-600">
                    ${Math.round(totalPotentialSavings).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="text-center pt-4">
                <div className="text-sm text-slate-500 mb-1">Estimated ROI</div>
                <div className="text-5xl font-bold text-purple-600">
                  {roiPercent}%
                </div>
              </div>
            </div>

            <p className="mt-6 text-xs text-slate-500 text-center">
              * Calculations are estimates based on industry averages. Actual savings may vary.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
